import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useIntl, FormattedMessage } from 'react-intl';
import * as profileParser from '../../../utils/billingInfoParser';
import { rootElementOptions } from '../../../utils/payments/fieldComponentOptions';
import { Button, LinkButton } from '../../common/Button';
import env from '../../../utils/env';
import { type AccountDiscountCoupon } from '../../../utils/api/storeApi';
import { type UserProfile } from '../../../utils/api/schema/profileSchema';
import UnsubscribeDialog from './components/UnsubscribeDialog';
import MobileStoreUnsubscribeDialog from './components/MobileStoreUnsubscribeDialog';
import RenewDialog from './components/RenewDialog';
import PaymentMethod from './components/PaymentMethod';
import PaymentIssue from './components/PaymentIssue';
import PayPalDeprecationBanner from './components/PayPalDeprecationBanner';
import ChangeMethodDialog from './components/ChangeMethodDialog';
import moneyEmojiImage from '../../../../assets/icons/referral/money-with-wings-emoji.png';
import moneyEmojiImage2x from '../../../../assets/icons/referral/money-with-wings-emoji@2x.png';
import styles from './BillingInfo.scss';

const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY).catch((error: unknown) => {
  console.error("Couldn't load Stripe:", error);

  return null;
});

interface BillingInfoBlockProps {
  profile: UserProfile;
  premiumLink: string;
  coupon: AccountDiscountCoupon;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- 3 SP of refactoring this file alone
const BillingInfo = ({ profile, premiumLink, coupon }: BillingInfoBlockProps) => {
  const intl = useIntl();

  const planName = profileParser.resolvePlanName(profile, intl);
  const specialPeriodBubble = profileParser.resolveSpecialPeriodBubble(profile, intl);
  const { isUpgradeCta, isRenewCta, isCancelCta, isMobileStoreCancelDialog, isSetappManageSubCta } =
    profileParser.resolveMainButton(profile);
  const { isDateShown, dateLabel, dateValue } = profileParser.resolveDateLine(profile, intl);
  const isPromoRedeemShown = profileParser.resolvePromocodeRedeem(profile, coupon, intl);
  const { isDiscountBannerShown, discountBannerText } = profileParser.resolveDiscountBanner(profile, coupon, intl);
  const { isPaymentMethodShown, isMethodEditable, hasPaymentIssue } = profileParser.resolvePaymentMethod(profile);
  const hasPayPalWarning = profileParser.hasPayPalDeprecationWarning(profile);

  const [isCancelDialogShow, setIsCancelDialogShow] = useState(false);
  const showUnsubscribeDialog = () => {
    setIsCancelDialogShow(true);
  };
  const onCancelUnsubscribe = () => {
    setIsCancelDialogShow(false);
  };

  const [isChangeMethodDialogShown, setIsChangeMethodDialogShown] = useState(false);
  const showChangeMethodDialog = () => {
    setIsChangeMethodDialogShown(true);
  };
  const hideChangeMethodDialog = () => {
    setIsChangeMethodDialogShown(false);
  };

  const [isRenewDialogShown, setIsRenewDialogShown] = useState(false);
  const onCancelRenew = () => {
    setIsRenewDialogShown(false);
  };

  return (
    <>
      {hasPayPalWarning ? (
        <PayPalDeprecationBanner
          onClick={() => {
            setIsChangeMethodDialogShown(true);
          }}
        />
      ) : null}

      <section className={styles.section}>
        <div className={styles.block}>
          <span className={styles.currentPlanLabel}>
            <FormattedMessage
              id="profile.billing.currentPlanLabel"
              defaultMessage="Current plan"
              description="Labels the current plan info"
            />
          </span>

          <div className={styles.firstLine}>
            <div className={styles.firstLineLeft}>
              <span className={styles.planName}>{planName}</span>
              {specialPeriodBubble.isShown ? (
                <span
                  className={`${styles.trialBubble} ${
                    specialPeriodBubble.tooltip ? styles.trialBubbleWithTooltip : ''
                  }`}
                >
                  {specialPeriodBubble.text}
                  {specialPeriodBubble.tooltip ? (
                    <span className={styles.tooltip}>{specialPeriodBubble.tooltip}</span>
                  ) : null}
                </span>
              ) : null}
            </div>

            <div className={styles.firstLineRight}>
              {isUpgradeCta ? (
                <LinkButton
                  buttonSize="small"
                  kind="primary"
                  href={premiumLink}
                  className={styles.firstLineRightButton}
                >
                  <FormattedMessage
                    id="profile.billing.upgradePlan"
                    defaultMessage="Upgrade plan"
                    description="Button that allows user to switch plan, i.e. from Monthly to Annual"
                  />
                </LinkButton>
              ) : null}
              {isRenewCta ? (
                <Button
                  buttonSize="small"
                  kind="primary"
                  className={styles.firstLineRightButton}
                  onClick={() => {
                    setIsRenewDialogShown(true);
                  }}
                >
                  <FormattedMessage
                    id="profile.billing.renewSubscription"
                    defaultMessage="Renew subscription"
                    description="Button for re-purchasing an already canceled subscription"
                  />
                </Button>
              ) : null}
              {isCancelCta ? (
                <Button
                  buttonSize="small"
                  kind="secondary"
                  className={styles.firstLineRightButton}
                  onClick={showUnsubscribeDialog}
                >
                  <FormattedMessage
                    id="profile.billing.cancelSubscription"
                    defaultMessage="Cancel subscription"
                    description="Button to cancel current subscription"
                  />
                </Button>
              ) : null}
              {isSetappManageSubCta ? (
                <LinkButton
                  buttonSize="small"
                  kind="secondary"
                  href="https://my.setapp.com/subscription"
                  className={styles.firstLineRightButton}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage
                    id="profile.billing.manageSetAppSubscription"
                    defaultMessage="Manage subscription"
                    description="Link to SetApp user’s cabinet, shown only for users with SetApp subscription"
                  />
                </LinkButton>
              ) : null}
            </div>
          </div>

          {isDateShown ? (
            <div className={styles.dateLine}>
              <span className={styles.dateLineLabel}>{dateLabel}</span>
              <span className={styles.dateLineValue}>{dateValue}</span>
            </div>
          ) : null}

          {isPromoRedeemShown ? (
            <div className={styles.redeemLine}>
              <LinkButton
                buttonSize="small"
                kind="secondary"
                href={`${env.SITE_URL}/redeem/`}
                className={styles.redeemLineButton}
              >
                <FormattedMessage
                  id="profile.billing.redeemPromoCode"
                  defaultMessage="Redeem a promocode"
                  description="Link to a promo code redeem page"
                />
              </LinkButton>
            </div>
          ) : null}

          {isDiscountBannerShown ? (
            <div className={styles.referralBanner}>
              <img
                src={moneyEmojiImage}
                srcSet={`${moneyEmojiImage2x} 2x`}
                width="24"
                height="24"
                alt="Money with wings emoji"
              />
              <span>{discountBannerText}</span>
            </div>
          ) : null}

          {isPaymentMethodShown ? (
            <>
              <div className={styles.divider} />

              <div className={styles.paymentMethod}>
                <h3 className={styles.paymentMethodTitle}>
                  <FormattedMessage
                    id="profile.billing.paymentMethodSubtitle"
                    defaultMessage="Payment method"
                    description="Title of the section displaying payment method, i.e. card, PayPal..."
                  />
                </h3>

                <div className={styles.methodInfo}>
                  <PaymentMethod paymentMethod={profile.payment_data?.payment_method} />

                  {isMethodEditable ? (
                    <Button
                      kind="secondary"
                      buttonSize="small"
                      className={styles.changeButton}
                      onClick={showChangeMethodDialog}
                    >
                      <FormattedMessage
                        id="profile.billing.changePaymentMethodButton"
                        defaultMessage="Change"
                        description="Button that allows to change payment method (card, PayPal, ...) for the next charge"
                      />
                    </Button>
                  ) : null}
                </div>
              </div>

              {hasPaymentIssue ? <PaymentIssue /> : null}
            </>
          ) : null}
        </div>
      </section>

      <UnsubscribeDialog
        profile={profile}
        visible={isCancelDialogShow ? !isMobileStoreCancelDialog : false}
        onCancel={onCancelUnsubscribe}
      />

      {isCancelDialogShow && isMobileStoreCancelDialog ? (
        <MobileStoreUnsubscribeDialog onCloseClick={onCancelUnsubscribe} />
      ) : null}
      {isMethodEditable || hasPayPalWarning ? (
        <Elements stripe={stripePromise} options={rootElementOptions}>
          <ChangeMethodDialog
            profile={profile}
            isDialogShown={isChangeMethodDialogShown}
            onCancel={hideChangeMethodDialog}
          />
        </Elements>
      ) : null}

      <RenewDialog profile={profile} isDialogShown={isRenewDialogShown} onClose={onCancelRenew} />
    </>
  );
};

export default BillingInfo;
